/**
 * Unified Tab System - Dashboard Tab Configuration
 * @module lib/tabs/config
 */

import { ROUTES } from "@app/config/constants";
import { ROUTES as RH_ROUTES } from "@features/rh/config/constants";
import type { TabIconName } from "./icons";

/**
 * Dashboard tab definition
 * Used for the home dashboard personal tabs
 */
export interface DashboardTabConfig {
  /** Unique identifier */
  readonly id: string;
  /** Display title */
  readonly title: string;
  /** Route path */
  readonly path: string;
  /** Icon name from registry */
  readonly iconName: TabIconName;
  /** Sort order */
  readonly order?: number;
  /** Required permission (optional) */
  readonly permission?: string;
}

/**
 * Dashboard tab definitions
 * These replace the hardcoded array in DashboardTabs.jsx
 */
export const DASHBOARD_TABS: readonly DashboardTabConfig[] = [
  {
    id: "home",
    title: "Accueil",
    path: ROUTES.APP.HOME,
    iconName: "Home",
    order: 10,
  },
  {
    id: "profile",
    title: "Mon Profil",
    path: ROUTES.APP.PROFILE,
    iconName: "UserCircle",
    order: 20,
  },
  {
    id: "conges",
    title: "Congés & Absences",
    path: RH_ROUTES.RH.CONGES.ME,
    iconName: "Calendar",
    order: 30,
  },
  {
    id: "pointage",
    title: "Feuille de Temps",
    path: RH_ROUTES.RH.POINTAGE.ME,
    iconName: "Clock4",
    order: 40,
  },
  {
    id: "planning",
    title: "Mon Planning",
    path: RH_ROUTES.RH.PLANNING.ME,
    iconName: "CalendarDays",
    order: 50,
  },
  {
    id: "contrats",
    title: "Mes Contrats",
    path: RH_ROUTES.RH.CONTRATS.ME,
    iconName: "FileText",
    order: 60,
  },
  {
    id: "documents",
    title: "Mes Documents",
    path: RH_ROUTES.RH.DOCUMENTS.ME,
    iconName: "FolderOpen",
    order: 70,
  },
  {
    id: "frais",
    title: "Mes Frais",
    path: RH_ROUTES.RH.NOTES_DE_FRAIS.ME,
    iconName: "Receipt",
    order: 80,
  },
] as const;

/**
 * Module tab defaults
 */
export const MODULE_TAB_DEFAULTS = {
  variant: "underline" as const,
  size: "sm" as const,
  responsiveBreakpoint: "lg",
  showIcons: true,
  showBadges: true,
} as const;

/**
 * Disabled reasons copy
 */
export const DISABLED_REASONS: Record<string, string> = {
  permission_denied: "Vous n'avez pas la permission d'accéder à cet onglet",
  not_self_data: "Cette section affiche uniquement vos propres données",
  not_manager: "Vous devez être responsable d'équipe pour accéder à cette section",
  no_department: "Votre département n'est pas défini",
} as const;

/**
 * Sort tabs by order, with fallback to alphabetical
 */
export function sortTabs<T extends { order?: number; title: string }>(tabs: T[]): T[] {
  return [...tabs].sort((a, b) => {
    const orderA = a.order ?? 999;
    const orderB = b.order ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.title.localeCompare(b.title);
  });
}
